import React, { useEffect, useState, useCallback, useMemo } from "react";
import StaffShell from "@/components/layout/StaffShell";
import { staffApi, formatApiError, formatIDR, CURRENCY_OPTIONS } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { hasPermission } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Copy, Trash2, ChevronLeft, ChevronRight, ChevronDown, CheckCircle2, Search, X } from "lucide-react";
import ImageUpload from "@/components/ImageUpload";

const blankForm = () => ({
  // Step 1
  service_type: "single",
  category_id: "",
  subcategory_id: "",
  name: "",
  required_staff: 1,
  // Iter-379 · Requires Assistant — mutex with required_staff > 1.
  // When true, Processing Time (min) must be > 0 and the calendar
  // only holds the assistant for `processing_time_min` minutes.
  requires_assistant: false,
  // Iter-384 · Assistant commission — required when requires_assistant
  // is on. Type is "percent" or "amount" (flat IDR). The ledger uses
  // this at order close to pay the assistant on top of the primary.
  assistant_commission_value: 0,
  assistant_commission_type: "percent",
  processing_time_min: 0,
  service_time_min: 60,
  show_online: true,
  visibility: "all",
  image_url: "",
  description: "",
  active: true,
  // Step 2
  branch_ids: [], // empty means "all"
  // Step 3
  pricing: { same_for_all: true, price: 0, deposit: 0, per_branch: {} },
  price: 0,
  // Step 4
  consumable_product_ids: [],
});

const STEPS = [
  { n: 1, label: "Details" },
  { n: 2, label: "Locations" },
  { n: 3, label: "Pricing" },
  { n: 4, label: "Products" },
];

export default function BrandServices() {
  const { staff } = useAuth();
  const canCreate = hasPermission(staff, "brand.services.create");
  const canEdit = hasPermission(staff, "brand.services.edit");
  const canDelete = hasPermission(staff, "brand.services.delete");
  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState([]);
  const [branches, setBranches] = useState([]);
  const [products, setProducts] = useState([]);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(blankForm());

  const load = useCallback(async () => {
    try {
      const [s, c, b, p] = await Promise.all([
        staffApi.get("/services", { params: { active_only: false } }),
        staffApi.get("/categories"),
        staffApi.get("/branches"),
        staffApi.get("/products"),
      ]);
      setRows(s.data); setCats(c.data); setBranches(b.data); setProducts(p.data);
    } catch (err) { toast.error(formatApiError(err)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const catMap = useMemo(() => Object.fromEntries(cats.map((c) => [c.id, c])), [cats]);
  const branchMap = useMemo(() => Object.fromEntries(branches.map((b) => [b.id, b])), [branches]);

  // ---------- Compact search / pagination ----------
  //
  // Name text filter, category + sub-category selects, plus paginated rows so
  // the operator can scan hundreds of services on one screen (Mindbody parity).
  // `rowsPerPage` persists in localStorage so the operator's chosen density is
  // sticky across visits.
  const [nameFilter, setNameFilter] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [subFilter, setSubFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const v = parseInt(localStorage.getItem("brand-svc-rpp") || "25", 10);
    return [10, 25, 50, 100].includes(v) ? v : 25;
  });
  useEffect(() => { localStorage.setItem("brand-svc-rpp", String(rowsPerPage)); }, [rowsPerPage]);
  useEffect(() => { setPage(1); }, [nameFilter, catFilter, subFilter, statusFilter, rowsPerPage]);

  const subcatMap = useMemo(() => {
    const m = {};
    for (const c of cats) for (const s of (c.subcategories || [])) m[s.id] = { ...s, catId: c.id };
    return m;
  }, [cats]);

  const filteredRows = useMemo(() => {
    const n = nameFilter.trim().toLowerCase();
    // Resolve the selected category's canonical name so legacy services (with
    // only `category` string and no `category_id`) still match. `Aquashine
    // Skinbooster` for example predates the category_id migration.
    const selectedCatName = catFilter === "all" ? null : (cats.find((c) => c.id === catFilter)?.name || "").trim().toLowerCase();
    const list = rows.filter((s) => {
      if (n && !(s.name || "").toLowerCase().includes(n)) return false;
      if (catFilter !== "all") {
        const svcCatMatch = s.category_id === catFilter
          || (!s.category_id && selectedCatName && (s.category || "").trim().toLowerCase() === selectedCatName);
        if (!svcCatMatch) return false;
      }
      if (subFilter !== "all" && s.subcategory_id !== subFilter) return false;
      if (statusFilter === "active" && !s.active) return false;
      if (statusFilter === "inactive" && s.active) return false;
      return true;
    });
    // Iter-338 — Cluster services BY CATEGORY inside `filteredRows` BEFORE
    // pagination so a category cannot fragment across pages (page 1 showing
    // "Massages · 3 services" and page 2 showing another "Massages · 1
    // service" — the operator saw this as a duplicate group). With this sort,
    // all services in one category are contiguous, and a header only appears
    // on a second page if the category itself exceeds `rowsPerPage` rows.
    // Iter-335 pin still applies: Uncategorized comes first, then real
    // categories alphabetically by canonical name (case-insensitive, singular/
    // plural collapsed) so duplicate-named category rows would also merge.
    const catMapLocal = Object.fromEntries(cats.map((c) => [c.id, c]));
    const isUncat = (s) => !catMapLocal[s.category_id] && !(s.category && String(s.category).trim());
    const normCatName = (s) => {
      const resolved = catMapLocal[s.category_id];
      const label = resolved?.name || s.category || "";
      return String(label).trim().toLowerCase().replace(/s$/, "");
    };
    return list.slice().sort((a, b) => {
      const au = isUncat(a), bu = isUncat(b);
      if (au && !bu) return -1;
      if (!au && bu) return 1;
      if (!au && !bu) {
        const na = normCatName(a), nb = normCatName(b);
        if (na < nb) return -1;
        if (na > nb) return 1;
      }
      // Within a category (or within uncategorized), keep the visible list
      // stable and readable by sorting on service name.
      const an = (a.name || "").toLowerCase();
      const bn = (b.name || "").toLowerCase();
      if (an < bn) return -1;
      if (an > bn) return 1;
      return 0;
    });
  }, [rows, nameFilter, catFilter, subFilter, statusFilter, cats]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / rowsPerPage));
  const clampedPage = Math.min(page, totalPages);
  const pagedRows = useMemo(
    () => filteredRows.slice((clampedPage - 1) * rowsPerPage, clampedPage * rowsPerPage),
    [filteredRows, clampedPage, rowsPerPage]
  );

  // ---------- Grouped Catalog View (Iter-331 · Iter-335 Uncategorized pin) ----------
  //
  // Group visible services by Category header. Within each category, services
  // are sorted by their sub-category name (blank sub-categories sink to the
  // bottom) and then by service name for stable ordering.
  //
  // Iter-335 surgical fix: any service missing a valid `category_id` (or whose
  // legacy `category` string does not resolve to a known category) is bucketed
  // into a single "Uncategorized" group that is PINNED to the TOP of the list
  // and AUTO-EXPANDED by default. This ensures newly cloned services (which
  // ship without a category_id — see backend `POST /services/{sid}/clone`) are
  // visible on the catalog immediately after the "Service cloned" toast, so
  // the operator can find them and move them into the right category.
  const UNCATEGORIZED_KEY = "__uncategorized__";
  const UNCATEGORIZED_LABEL = "Uncategorized";

  const [expandedCats, setExpandedCats] = useState(() => new Set([UNCATEGORIZED_KEY]));
  const toggleCat = useCallback((key) => {
    setExpandedCats((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const groupedRows = useMemo(() => {
    const buckets = new Map();
    for (const svc of pagedRows) {
      const resolvedCat = catMap[svc.category_id];
      // A service is Uncategorized when it has no valid category_id AND no
      // legacy `category` string mapping. This catches: fresh clones, imported
      // rows with dropped categories, and services whose category was deleted.
      const isUncat = !resolvedCat && !(svc.category && String(svc.category).trim());
      // Iter-337 — Defensive grouping-by-name: any two category rows that resolve
      // to the same normalised display name (case-insensitive, trimmed, singular
      // vs plural collapsed) share ONE bucket. Guarantees the catalog never
      // fragments into visually-duplicate "Massages"/"Massage" groups even if
      // legacy DB rows carry duplicate category records for the same label.
      const displayName = isUncat ? UNCATEGORIZED_LABEL : (resolvedCat?.name || svc.category || UNCATEGORIZED_LABEL);
      const normName = String(displayName).trim().toLowerCase().replace(/s$/, "");
      const key = isUncat ? UNCATEGORIZED_KEY : `name::${normName}`;
      if (!buckets.has(key)) buckets.set(key, { key, catName: displayName, services: [], isUncategorized: isUncat });
      buckets.get(key).services.push(svc);
    }
    // Sort: Uncategorized FIRST, then the rest alphabetically by name.
    const groups = Array.from(buckets.values()).sort((a, b) => {
      if (a.isUncategorized && !b.isUncategorized) return -1;
      if (!a.isUncategorized && b.isUncategorized) return 1;
      return a.catName.localeCompare(b.catName, undefined, { sensitivity: "base" });
    });
    // Sort services inside each group by sub-category, then by service name.
    for (const g of groups) {
      g.services.sort((a, b) => {
        const subA = (subcatMap[a.subcategory_id]?.name || "~").toLowerCase();
        const subB = (subcatMap[b.subcategory_id]?.name || "~").toLowerCase();
        if (subA !== subB) return subA.localeCompare(subB);
        return (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" });
      });
    }
    return groups;
  }, [pagedRows, catMap, subcatMap]);

  // Any search/filter is active → EVERY matching group must be expanded.
  // Categories are collapsed by default, so a service matching the search but
  // living inside a collapsed category used to render nowhere — the operator
  // then believed the catalog held only one "5 days Juicing" while the package
  // picker (a flat search) correctly showed both records.
  const filtersActive = Boolean(nameFilter.trim()) || catFilter !== "all" || subFilter !== "all" || statusFilter !== "all";
  useEffect(() => {
    if (!filtersActive) return;
    setExpandedCats(new Set(groupedRows.map((g) => g.key)));
  }, [filtersActive, groupedRows]);

  // Services whose normalised name (case/whitespace/punctuation-insensitive)
  // is shared by another service — flagged inline so real DB duplicates are
  // visible instead of hiding behind near-identical spellings.
  const duplicateNameKeys = useMemo(() => {
    const counts = new Map();
    for (const s of rows) {
      const k = String(s.name || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
      if (!k) continue;
      counts.set(k, (counts.get(k) || 0) + 1);
    }
    return new Set([...counts.entries()].filter(([, n]) => n > 1).map(([k]) => k));
  }, [rows]);
  const isDuplicateName = useCallback((s) => duplicateNameKeys.has(
    String(s.name || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, " ").trim()
  ), [duplicateNameKeys]);

  const resetFilters = () => {
    setNameFilter(""); setCatFilter("all"); setSubFilter("all"); setStatusFilter("all");
  };

  // Cascade: clear sub-category when category changes.
  useEffect(() => { setSubFilter("all"); }, [catFilter]);
  const subOptions = useMemo(() => {
    if (catFilter === "all") return [];
    const c = cats.find((x) => x.id === catFilter);
    return c?.subcategories || [];
  }, [catFilter, cats]);

  // Persist a location's currency choice made from Step 3. Optimistic update so the
  // Select stays snappy; the API call still happens in the background and we revert
  // if the server rejects the code.
  const updateBranchCurrency = async (bid, currency) => {
    const prev = branches;
    setBranches((list) => list.map((b) => (b.id === bid ? { ...b, currency } : b)));
    try {
      await staffApi.put(`/branches/${bid}/settings`, { currency });
      toast.success(`${branchMap[bid]?.name || "Branch"} currency set to ${currency}`);
    } catch (err) {
      setBranches(prev);
      toast.error(formatApiError(err));
    }
  };

  const openNew = () => { setEditing(null); setForm(blankForm()); setStep(1); setWizardOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    const f = { ...blankForm(), ...s };
    // `{...blankForm(), ...s}` retains blankForm.pricing when the legacy `s` has no
    // pricing object, which used to silently drop `s.price`. Merge fields explicitly
    // so a legacy top-level price still flows into the wizard.
    if (!s.pricing) {
      f.pricing = { same_for_all: true, price: Number(s.price) || 0, deposit: 0, per_branch: {} };
    } else {
      f.pricing = { same_for_all: true, price: Number(s.price) || 0, deposit: 0, per_branch: {}, ...s.pricing };
    }
    if (!f.service_time_min) f.service_time_min = s.duration_min || 60;
    // Backfill category_id from legacy services that stored only the category name.
    // Uses a fuzzy match (trim, lowercase, strip trailing 's') so historical singular
    // categories like "Facial" still map to the current "Facials".
    if (!f.category_id && s.category) {
      const norm = (v) => String(v || "").trim().toLowerCase().replace(/s$/, "");
      const catNorm = norm(s.category);
      const match = cats.find((c) => norm(c.name) === catNorm);
      if (match) f.category_id = match.id;
    }
    setForm(f); setStep(1); setWizardOpen(true);
  };

  const clone = async (s) => {
    try { await staffApi.post(`/services/${s.id}/clone`); toast.success("Service cloned"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.name}"?`)) return;
    try { await staffApi.delete(`/services/${s.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  // Iter-224 — Inline 'Online' quick toggle. Optimistically flips the
  // row state so the switch responds instantly, PATCHes the backend,
  // and rolls back with a toast on failure. Mirrors the "Show in
  // online menu" checkbox inside the Edit wizard — flipping either
  // updates the same `show_online` field.
  const toggleOnline = async (svc) => {
    if (!canEdit) return;
    const nextValue = !(svc.show_online !== false); // treat missing as visible
    const prev = rows;
    setRows((cur) => cur.map((r) => (r.id === svc.id ? { ...r, show_online: nextValue } : r)));
    try {
      const resp = await staffApi.patch(`/services/${svc.id}`, { show_online: nextValue });
      // Trust the server payload — it may normalise other fields.
      if (resp?.data?.id) {
        setRows((cur) => cur.map((r) => (r.id === svc.id ? { ...r, ...resp.data } : r)));
      }
      toast.success(nextValue ? "Visible online" : "Hidden from online menu");
    } catch (err) {
      setRows(prev);
      toast.error(formatApiError(err));
    }
  };

  const save = async () => {
    try {
      const cat = catMap[form.category_id];
      const reqAssist = !!form.requires_assistant;
      // Iter-379 · Requires Assistant: Processing Time becomes mandatory
      // (determines how long the assistant is blocked on the calendar).
      // Mutually exclusive with `required_staff > 1` — force staff count
      // back to 1 when requires_assistant is set so the mutex can never
      // be bypassed, even if a stale value sits in the form state.
      const procMin = parseInt(form.processing_time_min) || 0;
      if (reqAssist && procMin <= 0) {
        toast.error(
          "Processing time is required when the service requires an assistant. " +
          "This determines how long the assistant is blocked on the calendar.",
        );
        return;
      }
      // Iter-384 · Assistant commission is mandatory when the service
      // requires an assistant — otherwise the ledger has nothing to pay
      // out at close time. Value must be > 0 and type must be one of
      // "percent" / "amount".
      const acv = parseFloat(form.assistant_commission_value) || 0;
      const act = form.assistant_commission_type;
      if (reqAssist && (acv <= 0 || !["percent", "amount"].includes(act))) {
        toast.error(
          "Assistant commission value and type are required when the service requires an assistant.",
        );
        return;
      }
      const reqN = reqAssist
        ? 1
        : Math.max(1, Math.min(4, parseInt(form.required_staff) || 1));
      const payload = {
        ...form,
        category: cat?.name || form.category || "",
        duration_min: parseInt(form.service_time_min) || 60,
        service_time_min: parseInt(form.service_time_min) || 60,
        processing_time_min: procMin,
        required_staff: reqN,
        requires_assistant: reqAssist,
        assistant_commission_value: reqAssist ? acv : 0,
        assistant_commission_type: reqAssist ? act : "percent",
        price: parseFloat(form.pricing?.same_for_all ? (form.pricing?.price || 0) : 0),
        pricing: {
          ...form.pricing,
          price: parseFloat(form.pricing?.price || 0),
          deposit: parseFloat(form.pricing?.deposit || 0),
        },
      };
      if (editing) await staffApi.put(`/services/${editing.id}`, payload);
      else await staffApi.post("/services", payload);
      toast.success("Service saved");
      setWizardOpen(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const canNext = () => {
    if (step === 1) {
      const baseOk = form.name && form.category_id && form.service_time_min > 0;
      // Iter-379 UX: if "Requires Assistant" is on, Processing Time must be > 0
      // before advancing — backend already enforces this but we block the
      // Next button so the user is not trapped later in the wizard.
      if (!baseOk) return false;
      if (form.requires_assistant && !(parseInt(form.processing_time_min) > 0)) return false;
      // Iter-384 · Block Next when assistant commission is incomplete.
      if (form.requires_assistant) {
        const acv = parseFloat(form.assistant_commission_value) || 0;
        const act = form.assistant_commission_type;
        if (acv <= 0 || !["percent", "amount"].includes(act)) return false;
      }
      return true;
    }
    if (step === 2) return true; // empty allowed = all locations
    if (step === 3) {
      if (form.pricing?.same_for_all) return form.pricing.price >= 0;
      // per-branch: every selected branch must have price
      const bids = form.branch_ids?.length ? form.branch_ids : branches.map((b) => b.id);
      return bids.every((bid) => form.pricing?.per_branch?.[bid]?.price !== undefined && form.pricing.per_branch[bid].price !== "");
    }
    return true;
  };

  return (
    <StaffShell context="brand">
      <div className="flex items-end justify-between mb-4">
        <div>
          <p className="tracking-overline text-xs text-muted-foreground">BRAND CATALOG</p>
          <h1 className="text-3xl font-semibold mt-1" data-testid="services-title">Services</h1>
          <p className="text-sm text-muted-foreground">Services with future bookings cannot be deleted.</p>
        </div>
        {canCreate && (
          <Button onClick={openNew} data-testid="new-service-btn" className="gap-1"><Plus className="w-4 h-4" /> New service</Button>
        )}
      </div>

      {/* Compact filter bar — inline name search, category + sub-category
          selects, and a status pill filter. Reset chip appears once any
          filter is active so the operator always has a one-click clear. */}
      <Card className="rounded-lg mb-3">
        <CardContent className="p-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[220px] max-w-sm">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={nameFilter}
                onChange={(e) => setNameFilter(e.target.value)}
                placeholder="Search service name…"
                className="pl-8 h-8 text-sm"
                data-testid="svc-name-filter"
              />
            </div>
            <Select value={catFilter} onValueChange={setCatFilter}>
              <SelectTrigger className="h-8 text-xs w-[160px]" data-testid="svc-cat-filter"><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {cats.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
              </SelectContent>
            </Select>
            <Select value={subFilter} onValueChange={setSubFilter} disabled={catFilter === "all" || subOptions.length === 0}>
              <SelectTrigger className="h-8 text-xs w-[160px]" data-testid="svc-sub-filter">
                <SelectValue placeholder={catFilter === "all" ? "Select category first" : subOptions.length ? "Sub-category" : "No sub-categories"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All sub-categories</SelectItem>
                {subOptions.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-8 text-xs w-[120px]" data-testid="svc-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
            {(nameFilter || catFilter !== "all" || subFilter !== "all" || statusFilter !== "all") && (
              <Button variant="ghost" size="sm" onClick={resetFilters} className="h-8 gap-1 text-xs" data-testid="svc-filter-reset">
                <X className="w-3.5 h-3.5" /> Reset
              </Button>
            )}
            <div className="ml-auto text-xs text-muted-foreground" data-testid="svc-result-count">
              Showing <span className="font-medium text-foreground">{filteredRows.length === 0 ? 0 : (clampedPage - 1) * rowsPerPage + 1}
              –{Math.min(clampedPage * rowsPerPage, filteredRows.length)}</span> of <span className="font-medium text-foreground">{filteredRows.length}</span>
              {filteredRows.length !== rows.length && <> · <span className="text-muted-foreground">{rows.length} total</span></>}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-lg">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="h-8">
                <TableHead className="py-1.5 text-[11px] uppercase tracking-wider">Name</TableHead>
                <TableHead className="py-1.5 text-[11px] uppercase tracking-wider">Category</TableHead>
                <TableHead className="py-1.5 text-[11px] uppercase tracking-wider">Sub-Category</TableHead>
                <TableHead className="py-1.5 text-[11px] uppercase tracking-wider">Type</TableHead>
                <TableHead className="py-1.5 text-[11px] uppercase tracking-wider">Duration</TableHead>
                <TableHead className="py-1.5 text-[11px] uppercase tracking-wider">Online</TableHead>
                <TableHead className="py-1.5 text-[11px] uppercase tracking-wider">Price</TableHead>
                <TableHead className="py-1.5 text-[11px] uppercase tracking-wider">Locations</TableHead>
                <TableHead className="py-1.5 text-[11px] uppercase tracking-wider">Status</TableHead>
                <TableHead className="py-1.5 text-[11px] uppercase tracking-wider text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pagedRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} className="text-center text-xs text-muted-foreground py-8" data-testid="svc-empty-row">
                    {rows.length === 0 ? "No services yet — click ‘New service’ to add one." : "No services match your filters."}
                  </TableCell>
                </TableRow>
              )}
              {groupedRows.map((group) => {
                const isOpen = expandedCats.has(group.key);
                return (
                  <React.Fragment key={group.key}>
                    <TableRow
                      className="h-9 bg-muted/60 hover:bg-muted cursor-pointer border-t border-border"
                      onClick={() => toggleCat(group.key)}
                      data-testid={`svc-cat-header-${group.key}`}
                    >
                      <TableCell colSpan={10} className="py-1.5">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); toggleCat(group.key); }}
                            className="inline-flex items-center justify-center w-5 h-5 rounded hover:bg-background/60 text-muted-foreground"
                            aria-label={isOpen ? `Collapse ${group.catName}` : `Expand ${group.catName}`}
                            data-testid={`svc-cat-chevron-${group.key}`}
                          >
                            {isOpen
                              ? <ChevronDown className="w-3.5 h-3.5" />
                              : <ChevronRight className="w-3.5 h-3.5" />}
                          </button>
                          <span className="text-xs font-semibold uppercase tracking-wider text-foreground">
                            {group.catName}
                          </span>
                          <span className="text-[11px] text-muted-foreground">
                            · {group.services.length} service{group.services.length === 1 ? "" : "s"}
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                    {isOpen && group.services.map((s) => (
                      <TableRow key={s.id} className="h-9 hover:bg-muted/40" data-testid={`svc-row-${s.id}`}>
                        <TableCell className="font-medium py-1.5 text-sm">
                          <div className="flex items-center gap-2 min-w-0 pl-6">
                            {s.image_url && (
                              <img
                                src={s.image_url.startsWith("http") ? s.image_url : `${window.location.origin}${s.image_url}`}
                                alt=""
                                className="w-6 h-6 rounded object-cover flex-shrink-0"
                              />
                            )}
                            <span className="truncate">{s.name}</span>
                            {isDuplicateName(s) && (
                              <span
                                className="flex-shrink-0 rounded-full bg-amber-100 text-amber-800 border border-amber-300 px-1.5 py-[1px] text-[10px] font-semibold uppercase tracking-wide"
                                title="Another service shares this name — likely a duplicate record. Deactivate or delete the one you don't use."
                                data-testid={`svc-duplicate-badge-${s.id}`}
                              >
                                Duplicate
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="py-1.5 text-xs text-muted-foreground">{catMap[s.category_id]?.name || s.category || "—"}</TableCell>
                        <TableCell className="py-1.5 text-xs text-muted-foreground">{subcatMap[s.subcategory_id]?.name || "—"}</TableCell>
                        <TableCell className="py-1.5 text-xs">{s.service_type === "couple" ? "Couple" : "Single"}{s.required_staff > 1 ? ` · ${s.required_staff}` : ""}</TableCell>
                        <TableCell className="py-1.5 text-xs whitespace-nowrap">{(s.service_time_min || s.duration_min || 60)} min{s.processing_time_min > 0 ? ` +${s.processing_time_min}` : ""}</TableCell>
                        <TableCell className="py-1.5">
                          {/* Iter-224 — Inline quick toggle synced with the
                              Edit-wizard 'Show in online menu' checkbox. Green
                              thumb-track when visible; grey when hidden so the
                              operator can eyeball online availability without
                              opening the wizard. */}
                          <Switch
                            checked={s.show_online !== false}
                            onCheckedChange={() => toggleOnline(s)}
                            disabled={!canEdit}
                            aria-label={`Online visibility for ${s.name}`}
                            className={
                              (s.show_online !== false
                                ? "data-[state=checked]:bg-emerald-500"
                                : "data-[state=unchecked]:bg-muted-foreground/30")
                            }
                            data-testid={`svc-online-toggle-${s.id}`}
                          />
                        </TableCell>
                        <TableCell className="py-1.5 text-xs whitespace-nowrap">{formatIDR(s.pricing?.price || s.price || 0)}{s.pricing?.same_for_all === false ? " *" : ""}</TableCell>
                        <TableCell className="py-1.5 text-xs">{s.branch_ids?.length ? `${s.branch_ids.length}` : "All"}</TableCell>
                        <TableCell className="py-1.5">
                          {s.active
                            ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 rounded-full border border-emerald-200 h-5 px-2 text-[10px]">Active</Badge>
                            : <Badge variant="secondary" className="rounded-full h-5 px-2 text-[10px]">Inactive</Badge>}
                        </TableCell>
                        <TableCell className="py-1 text-right">
                          <div className="inline-flex items-center gap-0">
                            {canEdit && <Button size="icon" variant="ghost" onClick={() => openEdit(s)} className="h-7 w-7" data-testid={`edit-svc-${s.id}`} title="Edit"><Pencil className="w-3.5 h-3.5" /></Button>}
                            {canCreate && <Button size="icon" variant="ghost" onClick={() => clone(s)} className="h-7 w-7" data-testid={`clone-svc-${s.id}`} title="Duplicate"><Copy className="w-3.5 h-3.5" /></Button>}
                            {canDelete && <Button size="icon" variant="ghost" onClick={() => remove(s)} className="h-7 w-7 text-destructive hover:text-destructive" data-testid={`delete-svc-${s.id}`} title="Delete"><Trash2 className="w-3.5 h-3.5" /></Button>}
                            {!canEdit && !canCreate && !canDelete && <span className="text-muted-foreground italic text-xs pr-2">Read-only</span>}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
          {/* Pagination footer */}
          {filteredRows.length > 0 && (
            <div className="flex items-center justify-between px-3 py-2 border-t border-border text-xs" data-testid="svc-pagination">
              <div className="flex items-center gap-2 text-muted-foreground">
                <span>Rows per page</span>
                <Select value={String(rowsPerPage)} onValueChange={(v) => setRowsPerPage(parseInt(v, 10))}>
                  <SelectTrigger className="h-7 w-[70px] text-xs" data-testid="svc-rows-per-page"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[10, 25, 50, 100].map((n) => (<SelectItem key={n} value={String(n)}>{n}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">Page {clampedPage} of {totalPages}</span>
                <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={clampedPage <= 1} data-testid="svc-page-prev"><ChevronLeft className="w-3.5 h-3.5" /></Button>
                <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={clampedPage >= totalPages} data-testid="svc-page-next"><ChevronRight className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={wizardOpen} onOpenChange={setWizardOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="svc-wizard">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit service" : "New service"}</DialogTitle>
            <DialogDescription>
              Configure the service&apos;s details, availability, pricing and add-ons across the 4 steps below.
            </DialogDescription>
          </DialogHeader>

          {/* Stepper */}
          <div className="flex items-center justify-between mb-4 -mt-2">
            {STEPS.map((s, i) => (
              <React.Fragment key={s.n}>
                <div className={`flex items-center gap-2 ${step >= s.n ? "text-foreground" : "text-muted-foreground"}`}>
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${step > s.n ? "bg-primary text-primary-foreground" : step === s.n ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>
                    {step > s.n ? <CheckCircle2 className="w-4 h-4" /> : s.n}
                  </div>
                  <span className="text-sm hidden sm:inline">{s.label}</span>
                </div>
                {i < STEPS.length - 1 && <div className={`flex-1 h-px mx-2 ${step > s.n ? "bg-primary" : "bg-border"}`} />}
              </React.Fragment>
            ))}
          </div>

          {step === 1 && <Step1 form={form} setForm={setForm} cats={cats} />}
          {step === 2 && <Step2 form={form} setForm={setForm} branches={branches} />}
          {step === 3 && <Step3 form={form} setForm={setForm} branches={branches} onBranchCurrencyChange={updateBranchCurrency} />}
          {step === 4 && <Step4 form={form} setForm={setForm} products={products} />}

          <div className="mt-6 flex items-center justify-between">
            <Button variant="ghost" onClick={() => step === 1 ? setWizardOpen(false) : setStep(step - 1)} data-testid="wizard-back-btn">
              <ChevronLeft className="w-4 h-4 mr-1" /> {step === 1 ? "Cancel" : "Back"}
            </Button>
            {step < 4 ? (
              <Button onClick={() => canNext() ? setStep(step + 1) : toast.error("Please complete required fields")} data-testid="wizard-next-btn">
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={save} data-testid="wizard-save-btn">{editing ? "Save changes" : "Create service"}</Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </StaffShell>
  );
}

/* ---------------- Step 1: Details ---------------- */
function Step1({ form, setForm, cats }) {
  const cat = cats.find((c) => c.id === form.category_id);
  const subs = cat?.subcategories || [];
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const setRequiredStaff = (n) => {
    // Toggle: clicking the active tile returns to the "1 staff" default.
    // Iter-379 · Mutex with Requires Assistant — picking a staff count
    // > 1 clears the assistant flag so the two paths can never both be
    // on at the same time.
    const next = form.required_staff === n ? 1 : n;
    setForm((f) => ({ ...f, required_staff: next, requires_assistant: false }));
  };

  // Iter-379 · Requires Assistant toggle. When ON, we also reset
  // required_staff back to 1 (the client-facing booking still picks
  // only the primary) and the mutex greys out the 2/3/4 buttons.
  const toggleRequiresAssistant = () => {
    setForm((f) => ({
      ...f,
      requires_assistant: !f.requires_assistant,
      required_staff: !f.requires_assistant ? 1 : f.required_staff,
    }));
  };

  const reqAssist = !!form.requires_assistant;

  return (
    <div className="grid gap-4">
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label>Service type</Label>
          <Select value={form.service_type} onValueChange={(v) => upd("service_type", v)}>
            <SelectTrigger data-testid="svc-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="single">Single</SelectItem>
              <SelectItem value="couple">Couple</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Category</Label>
          <Select value={form.category_id} onValueChange={(v) => { upd("category_id", v); upd("subcategory_id", ""); }}>
            <SelectTrigger data-testid="svc-category"><SelectValue placeholder="Choose category" /></SelectTrigger>
            <SelectContent>
              {cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Subcategory</Label>
          <Select value={form.subcategory_id || ""} onValueChange={(v) => upd("subcategory_id", v)} disabled={subs.length === 0}>
            <SelectTrigger data-testid="svc-subcategory"><SelectValue placeholder={subs.length ? "Choose subcategory" : "(none — add in Categories)"} /></SelectTrigger>
            <SelectContent>
              {subs.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Name of service</Label>
          <Input value={form.name} onChange={(e) => upd("name", e.target.value)} placeholder="e.g. Signature Facial" data-testid="svc-name" />
        </div>
      </div>

      <div>
        <Label className="mb-2 block">Staff requirements</Label>
        <div className="flex flex-wrap gap-2">
          {[2, 3, 4].map((n) => {
            const disabled = reqAssist;
            const active = !reqAssist && form.required_staff === n;
            return (
              <label
                key={n}
                aria-disabled={disabled ? "true" : "false"}
                className={
                  "flex items-center gap-2 border border-border rounded-full px-3 py-1.5 transition-colors " +
                  (disabled
                    ? "opacity-40 cursor-not-allowed bg-muted/40 text-muted-foreground"
                    : "cursor-pointer " + (active ? "bg-primary text-primary-foreground border-primary" : "hover:bg-secondary"))
                }
                data-testid={`req-staff-${n}`}
                onClick={(e) => { if (disabled) { e.preventDefault(); } }}
              >
                <Checkbox
                  checked={active}
                  disabled={disabled}
                  onCheckedChange={() => { if (!disabled) setRequiredStaff(n); }}
                  className="hidden"
                />
                Requires {n} staff members
              </label>
            );
          })}
          {/* Iter-379 · Requires Assistant — primary (doctor) is blocked
              for the full service time; the assistant on the calendar is
              held only for `processing_time_min` minutes then freed. */}
          <label
            className={
              "flex items-center gap-2 border border-border rounded-full px-3 py-1.5 cursor-pointer transition-colors " +
              (reqAssist ? "bg-primary text-primary-foreground border-primary" : "hover:bg-secondary")
            }
            data-testid="req-assistant"
            title="Primary is blocked for the full service; the assistant is held only for the processing time."
          >
            <Checkbox
              checked={reqAssist}
              onCheckedChange={toggleRequiresAssistant}
              className="hidden"
            />
            Requires Assistant
          </label>
          <span className="text-xs text-muted-foreground self-center">Default: 1 staff</span>
        </div>
        {reqAssist && (
          <p className="text-[11px] text-muted-foreground mt-2" data-testid="req-assistant-hint">
            Assistant is blocked on the calendar only for the Processing Time duration, starting at the same time as the appointment.
          </p>
        )}
        {reqAssist && (
          /* Iter-384 · Assistant commission inputs. Appear only when the
             Requires Assistant toggle is on; disappear when it's off. */
          <div className="mt-3 grid sm:grid-cols-2 gap-3 rounded-lg border border-border bg-muted/30 p-3" data-testid="assistant-commission-block">
            <div>
              <Label>
                Assistant commission value<span className="text-destructive"> *</span>
              </Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={form.assistant_commission_value}
                onChange={(e) => upd("assistant_commission_value", e.target.value)}
                data-testid="svc-assistant-commission-value"
                className={!(parseFloat(form.assistant_commission_value) > 0) ? "border-destructive" : ""}
                placeholder={form.assistant_commission_type === "amount" ? "e.g. 50000" : "e.g. 5"}
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Paid to the assigned assistant when the order is closed.
              </p>
            </div>
            <div>
              <Label>
                Assistant commission type<span className="text-destructive"> *</span>
              </Label>
              <Select
                value={form.assistant_commission_type}
                onValueChange={(v) => upd("assistant_commission_type", v)}
              >
                <SelectTrigger data-testid="svc-assistant-commission-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percent" data-testid="svc-assistant-commission-type-percent">% (percentage)</SelectItem>
                  <SelectItem value="amount" data-testid="svc-assistant-commission-type-amount">IDR (flat amount)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground mt-1">
                % applies to the pre-tax base price; IDR pays a flat amount.
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label>
            Processing time (min){reqAssist ? <span className="text-destructive"> *</span> : null}
          </Label>
          <Input
            type="number"
            value={form.processing_time_min}
            onChange={(e) => upd("processing_time_min", e.target.value)}
            data-testid="svc-processing"
            aria-required={reqAssist ? "true" : "false"}
            className={reqAssist && !(parseInt(form.processing_time_min) > 0) ? "border-destructive" : ""}
          />
          <p className="text-[11px] text-muted-foreground mt-1">
            {reqAssist
              ? "Required — this is how long the assistant is blocked on the calendar."
              : "Blocks calendar for staff prep. Not shown to client."}
          </p>
        </div>
        <div>
          <Label>Service time (min)</Label>
          <Input type="number" value={form.service_time_min} onChange={(e) => upd("service_time_min", e.target.value)} data-testid="svc-time" />
          <p className="text-[11px] text-muted-foreground mt-1">Duration shown to the client on booking emails.</p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div className="flex items-center gap-3 pt-6">
          <Switch checked={form.show_online} onCheckedChange={(v) => upd("show_online", v)} data-testid="svc-online" />
          <div><Label>Show in online menu</Label><p className="text-[11px] text-muted-foreground">Visible to customers on the booking site</p></div>
        </div>
        <div>
          <Label>Visibility</Label>
          <Select value={form.visibility} onValueChange={(v) => upd("visibility", v)}>
            <SelectTrigger data-testid="svc-visibility"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All customers</SelectItem>
              <SelectItem value="members_only">Members only</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-3">
      </div>

      <ImageUpload value={form.image_url} onChange={(url) => upd("image_url", url)} label="Service image (shown on online menu)" testid="svc-image" />

      <div>
        <Label>Description</Label>
        <Textarea rows={3} value={form.description} onChange={(e) => upd("description", e.target.value)} placeholder="Shown on the online booking page" data-testid="svc-desc" />
      </div>

      <div>
        <Label>Active</Label>
        <Select value={form.active ? "yes" : "no"} onValueChange={(v) => upd("active", v === "yes")}>
          <SelectTrigger data-testid="svc-active"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="yes">Yes — bookable</SelectItem>
            <SelectItem value="no">No — hidden and not bookable</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

/* ---------------- Step 2: Locations ---------------- */
function Step2({ form, setForm, branches }) {
  const toggle = (bid) => setForm((f) => ({ ...f, branch_ids: f.branch_ids.includes(bid) ? f.branch_ids.filter((x) => x !== bid) : [...f.branch_ids, bid] }));
  const isAll = form.branch_ids.length === 0;
  return (
    <div className="grid gap-3">
      <p className="text-sm text-muted-foreground">Select the locations that offer this service. Leave all unchecked to make it available at every location.</p>
      <div className="rounded-lg border border-border divide-y divide-border">
        <label className="flex items-center gap-3 p-3 cursor-pointer hover:bg-secondary" data-testid="branch-check-all">
          <Checkbox checked={isAll} onCheckedChange={() => setForm((f) => ({ ...f, branch_ids: [] }))} />
          <div><p className="text-sm font-medium">All locations</p><p className="text-xs text-muted-foreground">Offered at every branch</p></div>
        </label>
        {branches.map((b) => (
          <label key={b.id} className="flex items-center gap-3 p-3 cursor-pointer hover:bg-secondary" data-testid={`branch-check-${b.id}`}>
            <Checkbox checked={form.branch_ids.includes(b.id)} onCheckedChange={() => toggle(b.id)} />
            <div className="flex-1"><p className="text-sm font-medium">Cocoon · {b.name}</p><p className="text-xs text-muted-foreground">{b.address}</p></div>
          </label>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Step 3: Pricing ---------------- */
// A number input that keeps its own transient string state so the user can freely
// clear it and type new digits without React re-rendering a "0" between keystrokes
// (which was the source of the "0900000" bug). The parent still owns the canonical
// numeric value; we only echo it back when the user leaves the field or the parent
// value diverges from what's currently being typed.
function NumberInput({ value, onChange, min, testid, placeholder }) {
  const [draft, setDraft] = React.useState(
    value === 0 || value === undefined || value === null ? "" : String(value),
  );
  // Re-sync when the parent value changes externally (e.g. switching between
  // per-branch rows or opening the wizard for a different service).
  React.useEffect(() => {
    const asNum = parseFloat(draft);
    if (!Number.isFinite(asNum) || asNum !== Number(value)) {
      setDraft(value === 0 || value === undefined || value === null ? "" : String(value));
    }
  }, [value, draft]);

  const handleChange = (e) => {
    // Accept digits and an optional dot. Strip any leading zeros before the first
    // significant digit so "0900000" collapses to "900000" as the user types.
    const raw = String(e.target.value).replace(/[^\d.]/g, "");
    const normalized = raw.replace(/^0+(?=\d)/, "");
    setDraft(normalized);
    onChange(normalized === "" ? 0 : parseFloat(normalized) || 0);
  };

  const handleBlur = () => {
    // On blur, normalise empty → 0 so downstream consumers see a canonical number.
    if (draft === "") setDraft("");
    else setDraft(String(parseFloat(draft) || 0));
  };

  return (
    <Input
      type="text"
      inputMode="decimal"
      value={draft}
      onChange={handleChange}
      onBlur={handleBlur}
      min={min}
      placeholder={placeholder}
      data-testid={testid}
    />
  );
}

function Step3({ form, setForm, branches, onBranchCurrencyChange }) {
  const p = form.pricing || { same_for_all: true, price: 0, deposit: 0, per_branch: {}, currency: "IDR" };
  const setSame = (v) => setForm((f) => ({ ...f, pricing: { ...p, same_for_all: v === "yes" } }));
  const setP = (k, v) => setForm((f) => ({ ...f, pricing: { ...p, [k]: v } }));
  const setBranchP = (bid, k, v) => setForm((f) => ({
    ...f, pricing: { ...p, per_branch: { ...p.per_branch, [bid]: { ...(p.per_branch?.[bid] || {}), [k]: v } } }
  }));

  // Smart currency detection — driven by the Step 2 branch selection.
  //   • No branches selected (all locations) OR every selected branch
  //     shares the same currency → that currency is the default.
  //   • Multiple selected branches with mixed currencies → fall back to
  //     IDR (operators can still override per-branch below in "No" mode).
  // The label + Currency picker below reflects this instantly whenever
  // Step 2 toggles change, so a Bahrain-only service defaults to BHD.
  const smartDefaultCurrency = React.useMemo(() => {
    const selected = form.branch_ids?.length
      ? branches.filter((b) => form.branch_ids.includes(b.id))
      : [];
    if (selected.length === 0) return "IDR";
    const uniq = Array.from(new Set(selected.map((b) => (b.currency || "IDR"))));
    return uniq.length === 1 ? uniq[0] : "IDR";
  }, [form.branch_ids, branches]);

  // Keep the same-for-all currency in sync with the smart default whenever the
  // operator changes the branch mix in Step 2 (unless they explicitly picked
  // a currency during this wizard session — tracked by `_currency_touched`).
  React.useEffect(() => {
    if (!p.same_for_all) return;
    if (p._currency_touched) return;
    if ((p.currency || "IDR") !== smartDefaultCurrency) {
      setForm((f) => ({ ...f, pricing: { ...(f.pricing || p), currency: smartDefaultCurrency } }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [smartDefaultCurrency, p.same_for_all]);

  const sameCurrency = p.currency || smartDefaultCurrency || "IDR";

  // Wipe any stale per-branch overrides. Useful when a service was
  // originally set to different prices per branch and the operator now
  // wants a single canonical price — one tap clears the whole
  // `per_branch` map so it can't quietly override the same-for-all
  // value at checkout. Only surfaced when there's actually something
  // to wipe.
  const overrideCount = Object.keys(p.per_branch || {}).filter((k) => {
    const bp = p.per_branch[k] || {};
    return bp.price !== undefined && bp.price !== "" && bp.price !== null;
  }).length;
  const wipeOverrides = () => {
    if (overrideCount === 0) return;
    if (!window.confirm(`Wipe ${overrideCount} per-branch price override${overrideCount === 1 ? "" : "s"}? The Same-for-all price will apply everywhere.`)) return;
    setForm((f) => ({ ...f, pricing: { ...(f.pricing || p), per_branch: {} } }));
  };

  const shownBranches = form.branch_ids.length ? branches.filter((b) => form.branch_ids.includes(b.id)) : branches;

  return (
    <div className="grid gap-4">
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label>Same price for all locations?</Label>
          <Select value={p.same_for_all ? "yes" : "no"} onValueChange={setSame}>
            <SelectTrigger data-testid="same-price"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="yes">Yes</SelectItem>
              <SelectItem value="no">No — set per location</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {p.same_for_all && overrideCount > 0 && (
          <div className="flex flex-col justify-end">
            <button
              type="button"
              onClick={wipeOverrides}
              className="self-start rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-100"
              data-testid="wipe-branch-overrides-btn"
              title="Clear all per-branch price overrides — Same-for-all will apply everywhere."
            >
              Wipe {overrideCount} branch override{overrideCount === 1 ? "" : "s"}
            </button>
            <p className="mt-1 text-[10px] text-muted-foreground">
              Currently {overrideCount} branch{overrideCount === 1 ? "" : "es"} still ha{overrideCount === 1 ? "s" : "ve"} an overridden price from before.
            </p>
          </div>
        )}
      </div>

      {p.same_for_all ? (
        <div className="grid sm:grid-cols-[180px_1fr_1fr] gap-3">
          <div>
            <Label>Currency</Label>
            <Select
              value={sameCurrency}
              onValueChange={(v) => setForm((f) => ({ ...f, pricing: { ...(f.pricing || p), currency: v, _currency_touched: true } }))}
            >
              <SelectTrigger data-testid="svc-currency-all"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {CURRENCY_OPTIONS.map((c) => (
                  <SelectItem key={c.code} value={c.code} data-testid={`svc-currency-all-opt-${c.code}`}>
                    {c.code} — {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-1 text-[10px] text-muted-foreground" data-testid="svc-currency-hint">
              Default derived from selected location{form.branch_ids?.length === 1 ? "" : "s"}.
            </p>
          </div>
          <div>
            <Label>Price ({sameCurrency})</Label>
            <NumberInput
              value={p.price}
              onChange={(v) => setP("price", v)}
              min="0"
              testid="svc-price-all"
              placeholder="0"
            />
          </div>
          <div>
            <Label>Deposit ({sameCurrency}) — 0 for none</Label>
            <NumberInput
              value={p.deposit}
              onChange={(v) => setP("deposit", v)}
              min="0"
              testid="svc-deposit-all"
              placeholder="0"
            />
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-border divide-y divide-border">
          {shownBranches.map((b) => {
            const bp = p.per_branch?.[b.id] || {};
            const currency = b.currency || "IDR";
            return (
              <div key={b.id} className="p-3 grid grid-cols-1 md:grid-cols-[1fr_170px_140px_140px] gap-3 items-end" data-testid={`price-row-${b.id}`}>
                <div>
                  <p className="text-sm font-medium">Cocoon · {b.name}</p>
                  <p className="text-xs text-muted-foreground">{b.address}</p>
                </div>
                <div>
                  <Label className="text-[11px]">Currency</Label>
                  <Select
                    value={currency}
                    onValueChange={(v) => onBranchCurrencyChange && onBranchCurrencyChange(b.id, v)}
                  >
                    <SelectTrigger data-testid={`currency-${b.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {CURRENCY_OPTIONS.map((c) => (
                        <SelectItem key={c.code} value={c.code} data-testid={`currency-opt-${b.id}-${c.code}`}>
                          {c.code} — {c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[11px]">Price ({currency})</Label>
                  <NumberInput
                    value={bp.price}
                    onChange={(v) => setBranchP(b.id, "price", v)}
                    min="0"
                    testid={`price-${b.id}`}
                    placeholder="0"
                  />
                </div>
                <div>
                  <Label className="text-[11px]">Deposit ({currency})</Label>
                  <NumberInput
                    value={bp.deposit}
                    onChange={(v) => setBranchP(b.id, "deposit", v)}
                    min="0"
                    testid={`deposit-${b.id}`}
                    placeholder="0"
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ---------------- Step 4: Products used ---------------- */
function Step4({ form, setForm, products }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const proProducts = products.filter((p) => p.is_pro);
  const chosen = form.consumable_product_ids || [];
  const productMap = Object.fromEntries(products.map((p) => [p.id, p]));

  const togglePick = (id) => setForm((f) => ({ ...f, consumable_product_ids: f.consumable_product_ids.includes(id) ? f.consumable_product_ids.filter((x) => x !== id) : [...f.consumable_product_ids, id] }));
  const removePicked = (id) => setForm((f) => ({ ...f, consumable_product_ids: f.consumable_product_ids.filter((x) => x !== id) }));

  return (
    <div className="grid gap-3">
      <p className="text-sm text-muted-foreground">Attach professional products consumed during the treatment. These are used for inventory deduction on completion.</p>

      <div className="rounded-lg border border-border">
        {chosen.length === 0 ? (
          <p className="p-8 text-sm text-muted-foreground text-center">No products yet.</p>
        ) : (
          <div className="divide-y divide-border">
            {chosen.map((id) => {
              const p = productMap[id];
              if (!p) return null;
              return (
                <div key={id} className="p-3 flex items-center gap-3" data-testid={`svc-prod-${id}`}>
                  <div className="w-8 h-8 rounded bg-secondary flex items-center justify-center text-xs">{p.name?.[0]}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{p.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{p.sku} · {p.size || ""}</p>
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => removePicked(id)}><Trash2 className="w-4 h-4" /></Button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Button variant="outline" onClick={() => setPickerOpen(true)} data-testid="add-product-btn" className="w-fit gap-1"><Plus className="w-4 h-4" /> Add product</Button>

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="max-w-lg" data-testid="product-picker-dialog">
          <DialogHeader>
            <DialogTitle>Add professional products</DialogTitle>
            <DialogDescription>Pick the products this service consumes. Amounts are optional.</DialogDescription>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">Showing all PRO products. Toggle any to attach.</p>
          <div className="rounded-lg border border-border divide-y divide-border max-h-80 overflow-y-auto">
            {proProducts.length === 0 && <p className="p-6 text-sm text-muted-foreground text-center">No PRO products. Add one in Products → mark as PRO.</p>}
            {proProducts.map((p) => (
              <label key={p.id} className="flex items-center gap-3 p-3 cursor-pointer hover:bg-secondary" data-testid={`picker-prod-${p.id}`}>
                <Checkbox checked={chosen.includes(p.id)} onCheckedChange={() => togglePick(p.id)} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{p.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{p.sku} · {formatIDR(p.price)}</p>
                </div>
              </label>
            ))}
          </div>
          <div className="flex justify-end"><Button onClick={() => setPickerOpen(false)}>Done</Button></div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
